import { Logger } from '../logger'
import { axiosSecure } from './api'

// Billing API (CAR-3710, self-serve model). Every call is scoped to a calibration center via
// ccId and uses customErrorHandling so the Billing store, not the global toast, decides how to
// surface failures. Errors are rethrown after logging, like scrubApi.

const logError = (method, error, params) => {
  Logger.error({ message: error, payload: { file: 'billingApi', method, params } })
}

/** GET /v1/billing/subscription?ccId= — drives the Billing tab (all six states). */
export const getBillingSubscription = async (ccId) => {
  try {
    const response = await axiosSecure.get('/v1/billing/subscription', { params: { ccId }, customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('getBillingSubscription', error, { ccId })
    throw error
  }
}

/** GET /v1/billing/invoices?ccId=&limit= — last N invoices from the local Stripe projection. */
export const getBillingInvoices = async (ccId, limit = 12) => {
  try {
    const response = await axiosSecure.get('/v1/billing/invoices', { params: { ccId, limit }, customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('getBillingInvoices', error, { ccId, limit })
    throw error
  }
}

/** GET /v1/billing/entitlements?ccId= — the modules this center may use (for gated-module prompts). */
export const getBillingEntitlements = async (ccId) => {
  try {
    const response = await axiosSecure.get('/v1/billing/entitlements', { params: { ccId }, customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('getBillingEntitlements', error, { ccId })
    throw error
  }
}

/**
 * POST /v1/billing/checkout-session — self-serve Stripe Checkout for a tier: signup,
 * reactivation, or converting an in-app trial (which keeps its remaining trial days).
 * Resolves to `{ url }`.
 */
export const createCheckoutSession = async (ccId, tier, promoCode) => {
  try {
    const body = promoCode ? { ccid: ccId, tier, promoCode } : { ccid: ccId, tier }
    const response = await axiosSecure.post('/v1/billing/checkout-session', body, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('createCheckoutSession', error, { ccId, tier, promoCode })
    throw error
  }
}

/**
 * POST /v1/billing/promo-codes/check — can this location use a promo code (a new center's first plan,
 * free for the code's days)? Always resolves to `{ valid, code, freeDays, tierIds, message }`; `tier` is optional.
 */
export const checkPromoCode = async (ccId, code, tier) => {
  try {
    const response = await axiosSecure.post('/v1/billing/promo-codes/check', { ccid: ccId, code, tier }, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('checkPromoCode', error, { ccId, code, tier })
    throw error
  }
}

/**
 * POST /v1/billing/upgrade — prorated, immediate upgrade of an existing Stripe subscription
 * (always self-serve; downgrades go through the portal). Resolves to `{ tier, message }`.
 */
export const upgradeSubscription = async (ccId, tier) => {
  try {
    const response = await axiosSecure.post('/v1/billing/upgrade', { ccid: ccId, tier }, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('upgradeSubscription', error, { ccId, tier })
    throw error
  }
}

/**
 * POST /v1/billing/end-trial — ends the Stripe trial now and starts the plan: the card on file is
 * charged at once. How a center gets going again while free trials are paused. Resolves to
 * `{ synced, stripeSubscriptionId, state, message }`.
 */
export const endTrialNow = async (ccId) => {
  try {
    const response = await axiosSecure.post('/v1/billing/end-trial', { ccid: ccId }, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('endTrialNow', error, { ccId })
    throw error
  }
}

/**
 * POST /v1/billing/sync — pulls the location's live subscription straight from Stripe and
 * reconciles it locally. Called on return from Checkout so the result shows immediately,
 * without depending on webhook delivery. Resolves to `{ synced, stripeSubscriptionId, state, message }`.
 */
export const syncBillingFromStripe = async (ccId) => {
  try {
    const response = await axiosSecure.post('/v1/billing/sync', { ccid: ccId }, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('syncBillingFromStripe', error, { ccId })
    throw error
  }
}

/**
 * POST /v1/billing/portal-session — opens the Stripe Customer Portal: card, invoices and
 * cancel-at-period-end, any time (month-to-month). No plan switching: downgrades go through support.
 * Resolves to `{ url }`.
 */
export const createPortalSession = async (ccId) => {
  try {
    const response = await axiosSecure.post('/v1/billing/portal-session', { ccid: ccId }, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('createPortalSession', error, { ccId })
    throw error
  }
}

// ── Downgrade requests (support ticket → CarADAS approval → smaller plan at the period end) ──

/**
 * GET /v1/billing/limits?ccId= — the plan's limits at this location and what is used (0173):
 * `{ tier, tierName, managedExternally, limits: [{ key, label, limit, display, used, remaining, period, periodStart,
 * upgradeTier, upgradeTierName, source }], lockedRos, enabledClientIds }`.
 */
export const getPlanLimits = async (ccId) => {
  try {
    const response = await axiosSecure.get('/v1/billing/limits', { params: { ccId }, customErrorHandling: true })
    return response?.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'billingApi', method: 'getPlanLimits', ccId } })
    throw error
  }
}

/**
 * POST /v1/billing/downgrade-requests — ask our team for a smaller plan. Always open (a plan without the
 * Support feature can still ask); creates the support ticket that carries the request. → the PlanChangeView.
 */
export const requestDowngrade = async (ccId, toTier, note) => {
  try {
    const response = await axiosSecure.post('/v1/billing/downgrade-requests', { ccid: ccId, toTier, note }, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'billingApi', method: 'requestDowngrade', ccId, toTier } })
    throw error
  }
}

/** POST /v1/billing/downgrade-requests/{id}/cancel — the center stops its own pending request. */
export const cancelDowngradeRequest = async (id) => {
  try {
    const response = await axiosSecure.post(`/v1/billing/downgrade-requests/${id}/cancel`, {}, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('cancelDowngradeRequest', error, { id })
    throw error
  }
}

/** GET /v1/billing/downgrade-requests/by-ticket/{ticketId} — the request a ticket carries, or null (204). */
export const getDowngradeRequestForTicket = async (ticketId) => {
  try {
    const response = await axiosSecure.get(`/v1/billing/downgrade-requests/by-ticket/${ticketId}`, { customErrorHandling: true })
    return response?.status === 204 ? null : (response?.data ?? null)
  } catch (error) {
    logError('getDowngradeRequestForTicket', error, { ticketId })
    throw error
  }
}

/** POST /v1/admin/billing/downgrade-requests/{id}/approve — CarADAS staff only; books it for the period end. */
export const approveDowngradeRequest = async (id) => {
  try {
    const response = await axiosSecure.post(`/v1/admin/billing/downgrade-requests/${id}/approve`, {}, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('approveDowngradeRequest', error, { id })
    throw error
  }
}

/** POST /v1/admin/billing/downgrade-requests/{id}/cancel — CarADAS staff only. */
export const adminCancelDowngradeRequest = async (id) => {
  try {
    const response = await axiosSecure.post(`/v1/admin/billing/downgrade-requests/${id}/cancel`, {}, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    logError('adminCancelDowngradeRequest', error, { id })
    throw error
  }
}
